import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Beef, LogOut, Home, Plus, CreditCard, Download, Check, X, User, Printer, QrCode, 
  Search, RefreshCw, Trash2, Users, Sparkles, CheckSquare, Square, UserPlus,
  Mail, Phone, Eye, Key, Shield, ChevronDown, MoreHorizontal
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useTheme } from '@/context/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;
const EMPLOYEE_PRESETS = {
  rider: { label: 'Rider consegne', permissions: ['orders.view_delivery','orders.update_delivery'] },
  counter: { label: 'Banconista', permissions: ['orders.view','orders.update','customers.view','points.manage','scanner.use'] },
  orders: { label: 'Gestore ordini', permissions: ['orders.view','orders.update','orders.print'] },
  custom: { label: 'Personalizzato', permissions: [] }
};
const PERMISSION_OPTIONS = [
  ['orders.view','Vedere tutti gli ordini'], ['orders.view_delivery','Vedere solo consegne'],
  ['orders.update','Modificare stati ordini'], ['orders.update_delivery','Aggiornare solo consegne'],
  ['orders.print','Stampare ordini'], ['customers.view','Vedere clienti'],
  ['points.manage','Gestire punti'], ['scanner.use','Usare scanner'],
  ['products.view','Vedere prodotti'], ['products.manage','Modificare prodotti'], ['offers.manage','Gestire offerte']
];


const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function PhysicalCards({ user, onLogout }) {
  const { isDark } = useTheme();
  
  // Theme classes
  const t = {
    bg: isDark ? 'bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#16213e]' : 'bg-gradient-to-br from-[#F9F7F2] via-[#FFFFFF] to-[#F5F3EE]',
    card: isDark ? 'bg-white/5 backdrop-blur-xl border-white/10' : 'bg-white shadow-lg border-gray-200',
    text: isDark ? 'text-white' : 'text-gray-900',
    textSecondary: isDark ? 'text-white/60' : 'text-gray-600',
    textMuted: isDark ? 'text-white/40' : 'text-gray-400',
    input: isDark ? 'bg-white/5 border-white/10 text-white placeholder-white/40' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400',
    header: isDark ? 'bg-black/20 backdrop-blur-xl border-white/10' : 'bg-white/80 backdrop-blur-xl border-gray-200',
    navLink: isDark ? 'text-white/60 hover:text-white' : 'text-gray-600 hover:text-gray-900',
    modal: isDark ? 'bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-white/10' : 'bg-white border-gray-200 shadow-2xl',
    glow1: isDark ? 'bg-[#C5A059]/10' : 'bg-[#C5A059]/5',
    glow2: isDark ? 'bg-[#5D1822]/20' : 'bg-[#5D1822]/5',
  };
  
  const [activeTab, setActiveTab] = useState('cards'); // 'cards' or 'employees'
  
  // Cards state
  const [cards, setCards] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedCards, setSelectedCards] = useState(new Set());
  
  // Employees state
  const [employees, setEmployees] = useState([]);
  const [showCreateEmployeeModal, setShowCreateEmployeeModal] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ full_name: '', email: '', phone: '', employee_profile: 'counter', permissions: [] });
  const [createdEmployee, setCreatedEmployee] = useState(null);
  
  // Generate modal
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateQuantity, setGenerateQuantity] = useState(10);
  const [generatePrefix, setGeneratePrefix] = useState('CARD');
  
  // Activate modal
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [activateMode, setActivateMode] = useState('existing');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [newCustomerData, setNewCustomerData] = useState({ full_name: '', email: '', phone: '' });
  const [activationResult, setActivationResult] = useState(null);
  
  // QR Preview modal
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCard, setQrCard] = useState(null);
  
  // Batch action modal
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cardsRes, customersRes, employeesRes] = await Promise.all([
        axios.get(`${API}/admin/physical-cards`, getAuthHeaders()),
        axios.get(`${API}/admin/customers`, getAuthHeaders()),
        axios.get(`${API}/admin/employees`, getAuthHeaders())
      ]);
      setCards(cardsRes.data);
      setCustomers(customersRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/admin/physical-cards/generate`,
        { quantity: generateQuantity, prefix: generatePrefix },
        getAuthHeaders()
      );
      toast.success(response.data.message);
      setShowGenerateModal(false);
      await fetchData();
    } catch (error) {
      toast.error('Errore nella generazione');
    } finally {
      setGenerating(false);
    }
  };

  const handleActivate = async () => {
    if (!selectedCard) return;
    
    try {
      let data = {};
      
      if (activateMode === 'new') {
        data = { create_new_customer: true, ...newCustomerData };
      } else {
        data = { user_id: selectedCustomerId };
      }
      
      const response = await axios.post(
        `${API}/admin/physical-cards/${selectedCard.id}/activate`,
        data,
        getAuthHeaders()
      );
      
      toast.success(response.data.message);
      setActivationResult(response.data);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'attivazione');
    }
  };

  const handleSelectCard = (cardId) => {
    const newSelected = new Set(selectedCards);
    if (newSelected.has(cardId)) {
      newSelected.delete(cardId);
    } else {
      newSelected.add(cardId);
    }
    setSelectedCards(newSelected);
  };

  const handleSelectAll = () => {
    const availableCards = filteredCards.filter(c => c.status === 'available');
    if (selectedCards.size === availableCards.length) {
      setSelectedCards(new Set());
    } else {
      setSelectedCards(new Set(availableCards.map(c => c.id)));
    }
  };

  const handleBatchDelete = async () => {
    setDeletingBatch(true);
    try {
      const response = await axios.post(
        `${API}/admin/physical-cards/batch-delete`,
        { card_ids: Array.from(selectedCards) },
        getAuthHeaders()
      );
      toast.success(`${response.data.deleted_count} carte eliminate!`);
      setSelectedCards(new Set());
      setShowBatchDeleteModal(false);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    } finally {
      setDeletingBatch(false);
    }
  };

  const handlePrintSelected = () => {
    const selectedCardsList = cards.filter(c => selectedCards.has(c.id));
    if (selectedCardsList.length === 0) {
      toast.error('Seleziona almeno una carta');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    let cardsHtml = selectedCardsList.map(card => `
      <div class="card">
        <div class="logo">🥩 Macelleria Borgese</div>
        <div class="qr">
          <img src="data:image/png;base64,${card.qr_code_data}" alt="QR Code" />
        </div>
        <div class="code">${card.card_code}</div>
      </div>
    `).join('');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Carte Fisiche - Macelleria Borgese</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
          .card {
            border: 2px solid #5D1822;
            border-radius: 12px;
            padding: 15px;
            text-align: center;
            page-break-inside: avoid;
          }
          .logo { color: #5D1822; font-size: 14px; font-weight: bold; margin-bottom: 8px; }
          .qr img { width: 120px; height: 120px; }
          .code { font-family: monospace; font-size: 12px; color: #5D1822; margin-top: 8px; }
        </style>
      </head>
      <body>
        <h1 style="color: #5D1822; text-align: center;">Carte Fidelity Selezionate</h1>
        <p style="text-align: center; color: #666;">Totale: ${selectedCardsList.length} carte</p>
        <div class="grid">${cardsHtml}</div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleShowQR = async (card) => {
    try {
      const response = await axios.get(`${API}/admin/physical-cards/${card.id}`, getAuthHeaders());
      setQrCard(response.data);
      setShowQRModal(true);
    } catch (error) {
      toast.error('Errore nel caricamento QR');
    }
  };

  const openActivateModal = (card) => {
    setSelectedCard(card);
    setActivateMode('existing');
    setSelectedCustomerId('');
    setNewCustomerData({ full_name: '', email: '', phone: '' });
    setActivationResult(null);
    setShowActivateModal(true);
  };

  const closeActivateModal = () => {
    setShowActivateModal(false);
    setSelectedCard(null);
    setActivationResult(null);
  };

  const handleDeactivate = async (cardId) => {
    if (!window.confirm('Sei sicuro di voler disattivare questa carta?')) return;
    try {
      await axios.post(`${API}/admin/physical-cards/${cardId}/deactivate`, {}, getAuthHeaders());
      toast.success('Carta disattivata con successo');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella disattivazione');
    }
  };

  // Employee functions
  const handleCreateEmployee = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/create-employee`,
        newEmployee,
        getAuthHeaders()
      );
      setCreatedEmployee(response.data);
      toast.success('Dipendente creato!');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleDeleteEmployee = async (employeeId, name) => {
    if (!window.confirm(`Eliminare il dipendente ${name}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/delete-employee/${employeeId}`, getAuthHeaders());
      toast.success('Dipendente eliminato');
      await fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const closeCreateEmployeeModal = () => {
    setShowCreateEmployeeModal(false);
    setNewEmployee({ full_name: '', email: '', phone: '', employee_profile: 'counter', permissions: [] });
    setCreatedEmployee(null);
  };

  const filteredCards = cards.filter(card => {
    const matchesSearch = card.card_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (card.user_name && card.user_name.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || card.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: cards.length,
    available: cards.filter(c => c.status === 'available').length,
    activated: cards.filter(c => c.status === 'activated').length
  };

  const availableFilteredCards = filteredCards.filter(c => c.status === 'available');
  const allAvailableSelected = availableFilteredCards.length > 0 && 
    availableFilteredCards.every(c => selectedCards.has(c.id));

  if (loading) {
    return (
      <div className={`min-h-screen ${t.bg} flex items-center justify-center`}>
        <div className={`${t.text} text-xl flex items-center gap-3`}>
          <div className="w-8 h-8 border-4 border-[#C5A059] border-t-transparent rounded-full animate-spin"></div>
          Caricamento...
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${t.bg}`}>
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-20 left-20 w-72 h-72 ${t.glow1} rounded-full blur-3xl animate-pulse`}></div>
        <div className={`absolute bottom-20 right-20 w-96 h-96 ${t.glow2} rounded-full blur-3xl animate-pulse`} style={{ animationDelay: '1s' }}></div>
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] ${isDark ? 'bg-gradient-to-r from-[#C5A059]/5 to-[#5D1822]/5' : 'bg-gradient-to-r from-[#C5A059]/3 to-[#5D1822]/3'} rounded-full blur-3xl`}></div>
      </div>

      {/* Header */}
      <header className={`relative z-10 border-b ${t.header}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <Link to="/admin" className={`flex items-center gap-2 ${t.navLink} transition-colors`}>
              <Home size={20} />
              <span className="hidden sm:inline text-sm">Dashboard</span>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C5A059] to-[#8B6914] flex items-center justify-center shadow-lg shadow-[#C5A059]/20">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className={`text-2xl font-bold ${t.text}`}>Gestione Carte</h1>
                <p className="text-sm text-[#C5A059]">Carte Fisiche & Dipendenti</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button onClick={onLogout} className={`p-2 rounded-lg ${t.textSecondary} hover:${isDark ? 'bg-white/10' : 'bg-gray-100'} transition-colors`}>
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8">
          <button
            onClick={() => setActiveTab('cards')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
              activeTab === 'cards'
                ? 'bg-gradient-to-r from-[#C5A059] to-[#8B6914] text-white shadow-lg shadow-[#C5A059]/30'
                : isDark ? 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
            }`}
          >
            <CreditCard size={20} />
            Carte Fisiche
            <span className={`ml-2 px-2 py-0.5 ${isDark ? 'bg-white/20' : 'bg-black/10'} rounded-full text-xs`}>{stats.total}</span>
          </button>
          <button
            onClick={() => setActiveTab('employees')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
              activeTab === 'employees'
                ? 'bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white shadow-lg shadow-[#4A5D23]/30'
                : isDark ? 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
            }`}
          >
            <Users size={20} />
            Dipendenti
            <span className={`ml-2 px-2 py-0.5 ${isDark ? 'bg-white/20' : 'bg-black/10'} rounded-full text-xs`}>{employees.length}</span>
          </button>
        </div>

        {activeTab === 'cards' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className={`relative overflow-hidden rounded-2xl ${isDark ? 'bg-gradient-to-br from-white/10 to-white/5 border-white/10' : 'bg-gradient-to-br from-gray-50 to-white border-gray-200'} backdrop-blur-xl border p-6`}>
                <div className={`absolute top-0 right-0 w-20 h-20 ${isDark ? 'bg-[#C5A059]/20' : 'bg-[#C5A059]/10'} rounded-full blur-2xl`}></div>
                <p className={`text-5xl font-bold ${t.text} mb-1`}>{stats.total}</p>
                <p className={`${t.textSecondary} text-sm`}>Totale Carte</p>
                <Sparkles className={`absolute bottom-4 right-4 w-8 h-8 ${isDark ? 'text-[#C5A059]/30' : 'text-[#C5A059]/20'}`} />
              </div>
              <div className={`relative overflow-hidden rounded-2xl ${isDark ? 'bg-gradient-to-br from-[#4A5D23]/20 to-[#4A5D23]/10 border-[#4A5D23]/30' : 'bg-gradient-to-br from-[#4A5D23]/10 to-[#4A5D23]/5 border-[#4A5D23]/20'} backdrop-blur-xl border p-6`}>
                <div className={`absolute top-0 right-0 w-20 h-20 ${isDark ? 'bg-[#4A5D23]/30' : 'bg-[#4A5D23]/20'} rounded-full blur-2xl`}></div>
                <p className="text-5xl font-bold text-[#7CB342] mb-1">{stats.available}</p>
                <p className={`${t.textSecondary} text-sm`}>Disponibili</p>
                <CreditCard className={`absolute bottom-4 right-4 w-8 h-8 ${isDark ? 'text-[#4A5D23]/30' : 'text-[#4A5D23]/20'}`} />
              </div>
              <div className={`relative overflow-hidden rounded-2xl ${isDark ? 'bg-gradient-to-br from-[#C5A059]/20 to-[#C5A059]/10 border-[#C5A059]/30' : 'bg-gradient-to-br from-[#C5A059]/10 to-[#C5A059]/5 border-[#C5A059]/20'} backdrop-blur-xl border p-6`}>
                <div className={`absolute top-0 right-0 w-20 h-20 ${isDark ? 'bg-[#C5A059]/30' : 'bg-[#C5A059]/20'} rounded-full blur-2xl`}></div>
                <p className="text-5xl font-bold text-[#C5A059] mb-1">{stats.activated}</p>
                <p className={`${t.textSecondary} text-sm`}>Attivate</p>
                <Check className={`absolute bottom-4 right-4 w-8 h-8 ${isDark ? 'text-[#C5A059]/30' : 'text-[#C5A059]/20'}`} />
              </div>
            </div>

            {/* Actions Bar */}
            <div className="flex flex-wrap gap-4 mb-6">
              <button
                onClick={() => setShowGenerateModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-[#C5A059] to-[#8B6914] text-white rounded-xl font-semibold flex items-center gap-2 hover:shadow-lg hover:shadow-[#C5A059]/30 transition-all hover:-translate-y-0.5"
              >
                <Plus size={20} />
                Genera Nuove Carte
              </button>
              
              {selectedCards.size > 0 && (
                <div className="flex gap-2 animate-fade-in">
                  <button
                    onClick={handlePrintSelected}
                    className={`px-4 py-3 ${isDark ? 'bg-white/10 text-white border-white/10' : 'bg-gray-100 text-gray-700 border-gray-200'} rounded-xl font-medium flex items-center gap-2 hover:${isDark ? 'bg-white/20' : 'bg-gray-200'} transition-all border`}
                  >
                    <Printer size={18} />
                    Stampa ({selectedCards.size})
                  </button>
                  <button
                    onClick={() => setShowBatchDeleteModal(true)}
                    className="px-4 py-3 bg-red-500/20 text-red-400 rounded-xl font-medium flex items-center gap-2 hover:bg-red-500/30 transition-all border border-red-500/30"
                  >
                    <Trash2 size={18} />
                    Elimina ({selectedCards.size})
                  </button>
                </div>
              )}
              
              <button
                onClick={fetchData}
                className={`px-4 py-3 ${isDark ? 'bg-white/5 text-white/80 border-white/10' : 'bg-gray-100 text-gray-600 border-gray-200'} rounded-xl font-medium flex items-center gap-2 hover:${isDark ? 'bg-white/10' : 'bg-gray-200'} transition-all border ml-auto`}
              >
                <RefreshCw size={18} />
                Aggiorna
              </button>
            </div>

            {/* Filters */}
            <div className={`rounded-2xl ${t.card} border p-4 mb-6`}>
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex-1 min-w-[250px]">
                  <div className="relative">
                    <Search className={`absolute left-4 top-1/2 -translate-y-1/2 ${t.textMuted} w-5 h-5`} />
                    <input
                      type="text"
                      placeholder="Cerca per codice o cliente..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={`w-full pl-12 pr-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#C5A059]/50 focus:ring-2 focus:ring-[#C5A059]/20 transition-all`}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  {['all', 'available', 'activated'].map(status => (
                    <button
                      key={status}
                      onClick={() => setFilterStatus(status)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        filterStatus === status
                          ? 'bg-[#C5A059] text-white shadow-lg shadow-[#C5A059]/30'
                          : isDark ? 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900 border border-gray-200'
                      }`}
                    >
                      {status === 'all' ? 'Tutte' : status === 'available' ? 'Disponibili' : 'Attivate'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Cards Grid */}
            <div className={`rounded-2xl ${t.card} border overflow-hidden`}>
              {filteredCards.length === 0 ? (
                <div className="p-16 text-center">
                  <div className={`w-24 h-24 mx-auto mb-6 rounded-full ${isDark ? 'bg-white/5' : 'bg-gray-100'} flex items-center justify-center`}>
                    <CreditCard className={`w-12 h-12 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                  </div>
                  <p className={`${t.textSecondary} text-lg mb-4`}>Nessuna carta trovata</p>
                  <button
                    onClick={() => setShowGenerateModal(true)}
                    className="px-6 py-3 bg-gradient-to-r from-[#C5A059] to-[#8B6914] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#C5A059]/30 transition-all"
                  >
                    Genera le prime carte
                  </button>
                </div>
              ) : (
                <>
                  {/* Table Header */}
                  <div className={`grid grid-cols-[auto,1fr,120px,1fr,140px,120px] gap-4 p-4 ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'} border-b ${t.textSecondary} text-sm font-medium`}>
                    <div className="flex items-center">
                      <button
                        onClick={handleSelectAll}
                        className={`p-1 hover:${isDark ? 'bg-white/10' : 'bg-gray-200'} rounded transition-colors`}
                        title="Seleziona tutte le disponibili"
                      >
                        {allAvailableSelected ? (
                          <CheckSquare size={20} className="text-[#C5A059]" />
                        ) : (
                          <Square size={20} />
                        )}
                      </button>
                    </div>
                    <div>Codice Carta</div>
                    <div>Stato</div>
                    <div>Cliente</div>
                    <div>Data Creazione</div>
                    <div className="text-right">Azioni</div>
                  </div>
                  
                  {/* Table Body */}
                  <div className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                    {filteredCards.map(card => (
                      <div 
                        key={card.id} 
                        className={`grid grid-cols-[auto,1fr,120px,1fr,140px,120px] gap-4 p-4 items-center hover:${isDark ? 'bg-white/5' : 'bg-gray-50'} transition-colors ${
                          selectedCards.has(card.id) ? (isDark ? 'bg-[#C5A059]/10' : 'bg-[#C5A059]/5') : ''
                        }`}
                      >
                        <div>
                          {card.status === 'available' && (
                            <button
                              onClick={() => handleSelectCard(card.id)}
                              className={`p-1 hover:${isDark ? 'bg-white/10' : 'bg-gray-200'} rounded transition-colors`}
                            >
                              {selectedCards.has(card.id) ? (
                                <CheckSquare size={20} className="text-[#C5A059]" />
                              ) : (
                                <Square size={20} className={t.textMuted} />
                              )}
                            </button>
                          )}
                        </div>
                        <div className="font-mono font-bold text-[#C5A059] text-lg">{card.card_code}</div>
                        <div>
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                            card.status === 'available' 
                              ? 'bg-[#4A5D23]/20 text-[#7CB342] border border-[#4A5D23]/30' 
                              : 'bg-[#C5A059]/20 text-[#C5A059] border border-[#C5A059]/30'
                          }`}>
                            {card.status === 'available' ? (
                              <><span className="w-2 h-2 rounded-full bg-[#7CB342] animate-pulse"></span> Disponibile</>
                            ) : (
                              <><Check size={12} /> Attivata</>
                            )}
                          </span>
                        </div>
                        <div className={isDark ? 'text-white/80' : 'text-gray-700'}>
                          {card.user_name ? (
                            <span className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-full ${isDark ? 'bg-[#5D1822]/30' : 'bg-[#5D1822]/10'} flex items-center justify-center`}>
                                <User size={14} className="text-[#C5A059]" />
                              </div>
                              {card.user_name}
                            </span>
                          ) : (
                            <span className={t.textMuted}>—</span>
                          )}
                        </div>
                        <div className={`${t.textSecondary} text-sm`}>
                          {new Date(card.created_at).toLocaleDateString('it-IT')}
                        </div>
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleShowQR(card)}
                            className={`p-2 rounded-lg ${isDark ? 'bg-white/5 hover:bg-white/10 text-white/60' : 'bg-gray-100 hover:bg-gray-200 text-gray-500'} hover:text-${isDark ? 'white' : 'gray-900'} transition-all`}
                            title="Vedi QR"
                          >
                            <QrCode size={16} />
                          </button>
                          {card.status === 'available' ? (
                            <button
                              onClick={() => openActivateModal(card)}
                              className="px-3 py-1.5 bg-[#4A5D23] text-white rounded-lg text-sm font-medium hover:bg-[#3A4A1A] transition-all"
                            >
                              Attiva
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDeactivate(card.id)}
                              className={`px-3 py-1.5 ${isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-500'} rounded-lg text-sm hover:${isDark ? 'bg-white/20' : 'bg-gray-200'} transition-all`}
                            >
                              Disattiva
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {activeTab === 'employees' && (
          <>
            {/* Employees Section */}
            <div className="mb-6 flex justify-between items-center">
              <div>
                <h2 className={`text-2xl font-bold ${t.text} mb-1`}>Gestione Dipendenti</h2>
                <p className={t.textSecondary}>Crea account per i tuoi collaboratori con accesso limitato</p>
              </div>
              <button
                onClick={() => setShowCreateEmployeeModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white rounded-xl font-semibold flex items-center gap-2 hover:shadow-lg hover:shadow-[#4A5D23]/30 transition-all hover:-translate-y-0.5"
              >
                <UserPlus size={20} />
                Nuovo Dipendente
              </button>
            </div>

            {/* Info Card */}
            <div className={`rounded-2xl ${isDark ? 'bg-gradient-to-r from-[#4A5D23]/20 to-[#2E3A16]/20 border-[#4A5D23]/30' : 'bg-gradient-to-r from-[#4A5D23]/10 to-[#2E3A16]/10 border-[#4A5D23]/20'} backdrop-blur-xl border p-6 mb-8`}>
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl ${isDark ? 'bg-[#4A5D23]/30' : 'bg-[#4A5D23]/20'} flex items-center justify-center flex-shrink-0`}>
                  <Shield className="w-6 h-6 text-[#7CB342]" />
                </div>
                <div>
                  <h3 className={`${t.text} font-semibold mb-2`}>Cosa possono fare i dipendenti?</h3>
                  <ul className={`${isDark ? 'text-white/70' : 'text-gray-600'} text-sm space-y-1`}>
                    <li>✓ Scansionare QR code e aggiungere punti</li>
                    <li>✓ Visualizzare lista clienti e carte</li>
                    <li>✓ Applicare sconti ai clienti</li>
                    <li className={t.textMuted}>✗ Non possono accedere alle impostazioni</li>
                    <li className={t.textMuted}>✗ Non possono creare/eliminare clienti</li>
                    <li className={t.textMuted}>✗ Non possono gestire carte fisiche</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Employees Grid */}
            <div className="grid gap-4">
              {employees.length === 0 ? (
                <div className={`rounded-2xl ${t.card} border p-16 text-center`}>
                  <div className={`w-24 h-24 mx-auto mb-6 rounded-full ${isDark ? 'bg-white/5' : 'bg-gray-100'} flex items-center justify-center`}>
                    <Users className={`w-12 h-12 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                  </div>
                  <p className={`${t.textSecondary} text-lg mb-4`}>Nessun dipendente ancora</p>
                  <button
                    onClick={() => setShowCreateEmployeeModal(true)}
                    className="px-6 py-3 bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white rounded-xl font-semibold"
                  >
                    Aggiungi il primo dipendente
                  </button>
                </div>
              ) : (
                employees.map(emp => (
                  <div key={emp.id} className={`rounded-2xl ${t.card} border p-6 flex items-center justify-between hover:${isDark ? 'bg-white/10' : 'bg-gray-50'} transition-all`}>
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#4A5D23] to-[#2E3A16] flex items-center justify-center text-white text-xl font-bold">
                        {emp.full_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className={`${t.text} font-semibold text-lg`}>{emp.full_name}</h3>
                        <div className={`flex items-center gap-4 ${t.textSecondary} text-sm mt-1`}>
                          <span className="flex items-center gap-1">
                            <Mail size={14} />
                            {emp.email}
                          </span>
                          {emp.phone && (
                            <span className="flex items-center gap-1">
                              <Phone size={14} />
                              {emp.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#4A5D23]/20 text-[#7CB342] border border-[#4A5D23]/30">
                        {EMPLOYEE_PRESETS[emp.employee_profile]?.label || 'Dipendente'}
                      </span>
                      <button
                        onClick={() => handleDeleteEmployee(emp.id, emp.full_name)}
                        className="p-2 rounded-lg text-red-400/60 hover:text-red-400 hover:bg-red-500/10 transition-all"
                        title="Elimina dipendente"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </main>

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${t.modal} border rounded-3xl max-w-md w-full p-8 shadow-2xl`}>
            <h3 className={`text-2xl font-bold ${t.text} mb-6 flex items-center gap-3`}>
              <div className="w-10 h-10 rounded-xl bg-[#C5A059]/20 flex items-center justify-center">
                <Plus size={20} className="text-[#C5A059]" />
              </div>
              Genera Nuove Carte
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block ${t.textSecondary} text-sm mb-2`}>Quantità</label>
                <input
                  type="number"
                  value={generateQuantity}
                  onChange={(e) => setGenerateQuantity(parseInt(e.target.value) || 10)}
                  className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#C5A059]/50`}
                  min="1"
                  max="100"
                />
              </div>
              <div>
                <label className={`block ${t.textSecondary} text-sm mb-2`}>Prefisso Codice</label>
                <input
                  type="text"
                  value={generatePrefix}
                  onChange={(e) => setGeneratePrefix(e.target.value.toUpperCase())}
                  className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#C5A059]/50`}
                  placeholder="CARD"
                />
                <p className={`${t.textMuted} text-xs mt-2`}>
                  Esempio: {generatePrefix}-0001, {generatePrefix}-0002...
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setShowGenerateModal(false)}
                className={`flex-1 px-4 py-3 ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-xl font-medium transition-all`}
              >
                Annulla
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-[#C5A059] to-[#8B6914] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#C5A059]/30 transition-all disabled:opacity-50"
              >
                {generating ? 'Generazione...' : `Genera ${generateQuantity} Carte`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activate Modal */}
      {showActivateModal && selectedCard && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${t.modal} border rounded-3xl max-w-md w-full p-8 shadow-2xl max-h-[90vh] overflow-y-auto`}>
            {!activationResult ? (
              <>
                <h3 className={`text-2xl font-bold ${t.text} mb-2`}>Attiva Carta</h3>
                <p className="text-[#C5A059] font-mono text-lg mb-6">{selectedCard.card_code}</p>
                
                <div className="flex gap-2 mb-6">
                  <button
                    onClick={() => setActivateMode('existing')}
                    className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                      activateMode === 'existing'
                        ? 'bg-[#C5A059] text-white'
                        : isDark ? 'bg-white/10 text-white/60 hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Cliente Esistente
                  </button>
                  <button
                    onClick={() => setActivateMode('new')}
                    className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                      activateMode === 'new'
                        ? 'bg-[#C5A059] text-white'
                        : isDark ? 'bg-white/10 text-white/60 hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Nuovo Cliente
                  </button>
                </div>
                
                {activateMode === 'existing' ? (
                  <select
                    value={selectedCustomerId}
                    onChange={(e) => setSelectedCustomerId(e.target.value)}
                    className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#C5A059]/50`}
                  >
                    <option value="">-- Seleziona Cliente --</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.full_name} ({c.email})</option>
                    ))}
                  </select>
                ) : (
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Nome Completo *"
                      value={newCustomerData.full_name}
                      onChange={(e) => setNewCustomerData(prev => ({ ...prev, full_name: e.target.value }))}
                      className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#C5A059]/50`}
                    />
                    <input
                      type="email"
                      placeholder="Email *"
                      value={newCustomerData.email}
                      onChange={(e) => setNewCustomerData(prev => ({ ...prev, email: e.target.value }))}
                      className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#C5A059]/50`}
                    />
                    <input
                      type="text"
                      placeholder="Telefono"
                      value={newCustomerData.phone}
                      onChange={(e) => setNewCustomerData(prev => ({ ...prev, phone: e.target.value }))}
                      className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#C5A059]/50`}
                    />
                  </div>
                )}
                
                <div className="flex gap-3 mt-6">
                  <button onClick={closeActivateModal} className={`flex-1 px-4 py-3 ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-xl font-medium`}>
                    Annulla
                  </button>
                  <button
                    onClick={handleActivate}
                    disabled={activateMode === 'existing' ? !selectedCustomerId : !newCustomerData.full_name || !newCustomerData.email}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white rounded-xl font-semibold disabled:opacity-50"
                  >
                    Attiva Carta
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#4A5D23]/30 flex items-center justify-center">
                  <Check className="w-10 h-10 text-[#7CB342]" />
                </div>
                <h3 className="text-2xl font-bold text-[#7CB342] mb-2">Carta Attivata!</h3>
                <p className={`${t.textSecondary} mb-4`}>{activationResult.customer.full_name}</p>
                
                {activationResult.temporary_password && (
                  <div className="bg-[#C5A059]/20 border border-[#C5A059]/30 rounded-xl p-4 mb-6 text-left">
                    <p className={`${t.textSecondary} text-sm mb-1`}>Password temporanea:</p>
                    <p className="font-mono text-xl text-[#C5A059]">{activationResult.temporary_password}</p>
                  </div>
                )}
                
                <button onClick={closeActivateModal} className="w-full px-4 py-3 bg-gradient-to-r from-[#C5A059] to-[#8B6914] text-white rounded-xl font-semibold">
                  Chiudi
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Employee Modal */}
      {showCreateEmployeeModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${t.modal} border rounded-3xl max-w-md w-full p-8 shadow-2xl`}>
            {!createdEmployee ? (
              <>
                <h3 className={`text-2xl font-bold ${t.text} mb-6 flex items-center gap-3`}>
                  <div className="w-10 h-10 rounded-xl bg-[#4A5D23]/30 flex items-center justify-center">
                    <UserPlus size={20} className="text-[#7CB342]" />
                  </div>
                  Nuovo Dipendente
                </h3>
                
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Nome Completo *"
                    value={newEmployee.full_name}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, full_name: e.target.value }))}
                    className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#4A5D23]/50`}
                  />
                  <input
                    type="email"
                    placeholder="Email *"
                    value={newEmployee.email}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, email: e.target.value }))}
                    className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#4A5D23]/50`}
                  />
                  <input
                    type="text"
                    placeholder="Telefono"
                    value={newEmployee.phone}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, phone: e.target.value }))}
                    className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#4A5D23]/50`}
                  />
                  <div>
                    <label className={`block ${t.textSecondary} text-sm mb-2`}>Ruolo operativo</label>
                    <select
                      value={newEmployee.employee_profile}
                      onChange={(e) => { const profile=e.target.value; setNewEmployee(prev => ({ ...prev, employee_profile: profile, permissions: EMPLOYEE_PRESETS[profile].permissions })); }}
                      className={`w-full px-4 py-3 ${t.input} border rounded-xl`}
                    >
                      {Object.entries(EMPLOYEE_PRESETS).map(([id,preset]) => <option key={id} value={id}>{preset.label}</option>)}
                    </select>
                  </div>
                  <div className={`rounded-xl border p-3 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <p className={`${t.text} font-semibold mb-2`}>Cosa può fare e vedere</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {PERMISSION_OPTIONS.map(([id,label]) => (
                        <label key={id} className={`flex items-center gap-2 text-sm ${t.textSecondary}`}>
                          <input type="checkbox" checked={(newEmployee.permissions || EMPLOYEE_PRESETS[newEmployee.employee_profile].permissions).includes(id)} onChange={(e) => {
                            const current=new Set(newEmployee.permissions || EMPLOYEE_PRESETS[newEmployee.employee_profile].permissions);
                            e.target.checked ? current.add(id) : current.delete(id);
                            setNewEmployee(prev => ({ ...prev, employee_profile:'custom', permissions:[...current] }));
                          }} />
                          {label}
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-red-500 mt-3">Editor, impostazioni, backup, reset e gestione dipendenti restano sempre vietati.</p>
                  </div>

                </div>
                
                <div className="flex gap-3 mt-8">
                  <button
                    onClick={closeCreateEmployeeModal}
                    className={`flex-1 px-4 py-3 ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-xl font-medium`}
                  >
                    Annulla
                  </button>
                  <button
                    onClick={handleCreateEmployee}
                    disabled={!newEmployee.full_name || !newEmployee.email}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white rounded-xl font-semibold disabled:opacity-50"
                  >
                    Crea Dipendente
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#4A5D23]/30 flex items-center justify-center">
                  <Check className="w-10 h-10 text-[#7CB342]" />
                </div>
                <h3 className="text-2xl font-bold text-[#7CB342] mb-2">Dipendente Creato!</h3>
                <p className={`${t.textSecondary} mb-4`}>{createdEmployee.employee.full_name}</p>
                
                <div className={`${isDark ? 'bg-white/10' : 'bg-gray-100'} rounded-xl p-4 mb-6 text-left space-y-2`}>
                  <div className="flex justify-between">
                    <span className={`${t.textSecondary} text-sm`}>Email:</span>
                    <span className={`${t.text} font-medium`}>{createdEmployee.employee.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={`${t.textSecondary} text-sm`}>Password:</span>
                    <span className="font-mono text-[#C5A059] font-bold">{createdEmployee.temporary_password}</span>
                  </div>
                </div>
                
                <div className="bg-[#C5A059]/20 border border-[#C5A059]/30 rounded-xl p-4 mb-6">
                  <p className="text-[#C5A059] text-sm">
                    ⚠️ Comunica queste credenziali al dipendente. Dovrà cambiare la password al primo accesso.
                  </p>
                </div>
                
                <button onClick={closeCreateEmployeeModal} className="w-full px-4 py-3 bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white rounded-xl font-semibold">
                  Chiudi
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* QR Preview Modal */}
      {showQRModal && qrCard && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${t.modal} border rounded-3xl max-w-sm w-full p-8 shadow-2xl text-center`}>
            <p className="font-mono text-2xl text-[#C5A059] font-bold mb-6">{qrCard.card_code}</p>
            
            <div className="bg-white p-6 rounded-2xl inline-block mb-6">
              <img
                src={`data:image/png;base64,${qrCard.qr_code_data}`}
                alt="QR Code"
                className="w-48 h-48"
              />
            </div>
            
            <p className={`${t.textSecondary} text-sm mb-6`}>
              {qrCard.status === 'activated' ? (
                <span className="text-[#C5A059]">Assegnata a: {qrCard.user_name}</span>
              ) : (
                <span className="text-[#7CB342]">✓ Disponibile per attivazione</span>
              )}
            </p>
            
            <div className="flex gap-3">
              <button onClick={() => setShowQRModal(false)} className={`flex-1 px-4 py-3 ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-xl font-medium`}>
                Chiudi
              </button>
              <button 
                onClick={() => {
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`
                    <!DOCTYPE html><html><head><title>QR - ${qrCard.card_code}</title>
                    <style>body{font-family:Arial;text-align:center;padding:20px}.card{border:2px solid #5D1822;border-radius:12px;padding:20px;max-width:300px;margin:0 auto}.logo{color:#5D1822;font-size:24px;font-weight:bold}.qr img{width:200px;height:200px}.code{font-family:monospace;font-size:18px;color:#5D1822}</style>
                    </head><body><div class="card"><div class="logo">🥩 Macelleria Borgese</div><div class="qr"><img src="data:image/png;base64,${qrCard.qr_code_data}"/></div><div class="code">${qrCard.card_code}</div></div><script>window.onload=function(){window.print()}</script></body></html>
                  `);
                  printWindow.document.close();
                }}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-[#C5A059] to-[#8B6914] text-white rounded-xl font-semibold flex items-center justify-center gap-2"
              >
                <Printer size={18} />
                Stampa
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Delete Modal */}
      {showBatchDeleteModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${t.modal} border border-red-500/30 rounded-3xl max-w-md w-full p-8 shadow-2xl`}>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <Trash2 className="w-8 h-8 text-red-400" />
            </div>
            <h3 className={`text-2xl font-bold ${t.text} text-center mb-2`}>Elimina Carte</h3>
            <p className={`${t.textSecondary} text-center mb-6`}>
              Stai per eliminare <span className="text-red-400 font-bold">{selectedCards.size}</span> carte. 
              Questa azione è irreversibile.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowBatchDeleteModal(false)}
                className={`flex-1 px-4 py-3 ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-xl font-medium`}
              >
                Annulla
              </button>
              <button
                onClick={handleBatchDelete}
                disabled={deletingBatch}
                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 disabled:opacity-50"
              >
                {deletingBatch ? 'Eliminazione...' : 'Elimina'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
